import { useState, useEffect, useRef } from "react"
import { GetCategory } from "../api"
import HCRender from "../hc-render"
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"

const BrowserCategory = ({
  category,
  level = 0,
  parentPath = "",
  onArticleSelect,
  onCategorySelect,
  expandedCategories = {},
  currentPath = {},
  fullPath = "",
  isMobile = false,
  preloadedData = false,
}) => {
  const texts = TextBundle({ id: "help-center" })
  const BrowserArticle = HCRender.getFragment("BrowserArticle")

  if (category.status === "draft" || category.content?.status === "draft") return null

  const categoryFullPath = parentPath
    ? `${parentPath}/${category.slug}`
    : category.slug

  const initialCategoryData = preloadedData
    ? { content: { categories: category.categories || [], articles: category.articles || [] } }
    : null

  const [categoryData, setCategoryData] = useState(initialCategoryData)
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(preloadedData)
  const categoryIdRef = useRef(category._id)

  const exactPath = currentPath.categoryPath || ""
  const articleSlug = currentPath.type === "article" ? currentPath.slug : null
  const exactPathParts = exactPath ? exactPath.split("/") : []
  const currentPathParts = categoryFullPath ? categoryFullPath.split("/") : []

  const isInExactPath =
    currentPathParts.length > 0 &&
    exactPathParts.length >= currentPathParts.length &&
    currentPathParts.every((part, index) => part === exactPathParts[index])

  // Also match when URL path is a suffix of the full tree path (e.g. URL has "essentials", tree has "featured/essentials")
  const pathSuffixMatch =
    exactPath.length > 0 &&
    (categoryFullPath === exactPath || categoryFullPath.endsWith("/" + exactPath))

  const containsActiveArticle =
    currentPath.type === "article" &&
    (currentPath.categoryPath === categoryFullPath || pathSuffixMatch)

  const isExactCategoryMatch =
    currentPath.type === "category" &&
    (currentPath.categoryPath === categoryFullPath || pathSuffixMatch)

  const isActive = isExactCategoryMatch
  const isHighlighted = isInExactPath || pathSuffixMatch

  const hasManualToggle = categoryFullPath in expandedCategories
  const isAutoExpanded = isInExactPath || pathSuffixMatch || containsActiveArticle
  const isExpanded = hasManualToggle
    ? expandedCategories[categoryFullPath]
    : isAutoExpanded

  useEffect(() => {
    if (category._id !== categoryIdRef.current) {
      categoryIdRef.current = category._id
      if (preloadedData) {
        setCategoryData({
          content: { categories: category.categories || [], articles: category.articles || [] },
        })
        setHasLoadedOnce(true)
      } else {
        setHasLoadedOnce(false)
        setCategoryData(null)
      }
    }
  }, [category._id, category.categories, category.articles, preloadedData])

  useEffect(() => {
    if (preloadedData || (hasLoadedOnce && categoryData) || !isExpanded) return

    const fetchCategoryData = async () => {
      if (!hasLoadedOnce) {
        setIsLoading(true)
        try {
          const data = await GetCategory(category.slug)
          setCategoryData(data)
          setHasLoadedOnce(true)
        } catch (error) {
          console.error("Error fetching category:", error)
        } finally {
          setIsLoading(false)
        }
      }
    }

    fetchCategoryData()
  }, [isExpanded, hasLoadedOnce, category.slug, categoryData, preloadedData])

  const handleToggle = (e) => {
    e.stopPropagation()
    if (onCategorySelect) onCategorySelect(category.slug, parentPath)
  }

  const indentationStyle = {
    paddingLeft: `${level * 20}px`,
    borderLeft: level > 2 ? "1px solid #e5e7eb" : "none",
    marginLeft: level > 0 ? "10px" : "0",
  }

  const hasContent = preloadedData || categoryData || isLoading

  const getFilteredSubcategories = () => {
    const subcategories = preloadedData
      ? category.categories
      : categoryData?.content?.categories
    if (!subcategories || subcategories.length === 0) return []
    return subcategories.filter(
      (s) => s.status !== "draft" && s.content?.status !== "draft"
    )
  }

  const getFilteredArticles = () => {
    const articles = preloadedData
      ? category.articles
      : categoryData?.content?.articles
    if (!articles || articles.length === 0) return []
    return articles.filter(
      (a) => a.status !== "draft" && a.content?.status !== "draft"
    )
  }

  const filteredSubcategories = getFilteredSubcategories()
  const filteredArticles = getFilteredArticles()

  return (
    <div>
      <div
        className={`flex items-center py-2 cursor-pointer ${
          isHighlighted ? "text-primary" : "text-text"
        }`}
        onClick={handleToggle}
        style={indentationStyle}
      >
        <Typography
          variant="body1_accent"
          className={`text-left my-0 ${
            isActive || isHighlighted ? "!text-primary" : "!text-text"
          }`}
        >
          {category.label}
        </Typography>

        <span className={`ml-2 ${isActive || isHighlighted ? "text-primary" : "text-text_secondary"}`}>
          {isExpanded ? (
            <ChevronUpIcon className="w-5 h-5" />
          ) : (
            <ChevronDownIcon className="w-5 h-5" />
          )}
        </span>

        {isLoading && (
          <Typography variant="txt1_dim" className="ml-2">
            {texts?.loading || "Loading..."}
          </Typography>
        )}
      </div>

      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: isExpanded ? "2000px" : "0px", opacity: isExpanded ? 1 : 0 }}
      >
        {hasContent && (
          <div>
            {filteredSubcategories.length > 0 && (
              <div>
                {filteredSubcategories.map((subCategory) => (
                  <BrowserCategory
                    key={subCategory._id}
                    category={subCategory}
                    level={level + 1}
                    parentPath={categoryFullPath}
                    onArticleSelect={onArticleSelect}
                    onCategorySelect={onCategorySelect}
                    expandedCategories={expandedCategories}
                    currentPath={currentPath}
                    fullPath={`${fullPath}/${category.slug}`}
                    isMobile={isMobile}
                    preloadedData={preloadedData}
                  />
                ))}
              </div>
            )}

            {filteredArticles.length > 0 && BrowserArticle && (
              <div className="articles">
                {filteredArticles.map((article) => {
                  const urlCategoryPath = currentPath.categoryPath || ""
                  const categoryPathMatches =
                    categoryFullPath === urlCategoryPath ||
                    (urlCategoryPath && categoryFullPath.endsWith("/" + urlCategoryPath))
                  const isArticleActive =
                    currentPath.type === "article" &&
                    articleSlug === article.slug &&
                    (isInExactPath || categoryPathMatches)

                  return (
                    <BrowserArticle
                      key={article._id}
                      article={article}
                      parentPath={categoryFullPath}
                      level={level}
                      onArticleSelect={onArticleSelect}
                      isActive={isArticleActive}
                      currentPath={currentPath}
                    />
                  )
                })}
              </div>
            )}

            {filteredSubcategories.length === 0 && filteredArticles.length === 0 && (
              <div
                style={{ ...indentationStyle, paddingLeft: `${(level + 1) * 20}px` }}
                className="py-2 text-text_secondary"
              >
                {texts?.empty_category || "No content in this category"}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default BrowserCategory
